import { ref, computed } from "vue";
import api from "@/services/api";
import { useCurrentUser } from "@/composables/useCurrentUser";

interface Message {
  id: number;
  subject: string;
  body: any[];
}

const messages = ref<Message[]>([]);
const readMessageIds = ref<number[]>([]);
const hasFetched = ref(false);
const voucherCache = ref<Record<string, string>>({});

export function useMessages() {
  const fetchMessages = async () => {
    if (hasFetched.value) return;
    hasFetched.value = true;

    const res = await api.get("/messages?populate=*&sort=updatedAt:desc");
    messages.value = res.data.data.map((e: any) => ({
      id: e.id,
      subject: e.attributes.subject,
      body: e.attributes.body,
    }));

    loadReadMessageIds();
  };

  const loadReadMessageIds = () => {
    const stored = localStorage.getItem("readMessageIds");
    readMessageIds.value = stored ? JSON.parse(stored) : [];
  };

  const markAsRead = (id: number) => {
    if (!readMessageIds.value.includes(id)) {
      readMessageIds.value.push(id);
      localStorage.setItem("readMessageIds", JSON.stringify(readMessageIds.value));
    }
  };

  const getVoucherCode = async (messageId: number, campaign?: string) => {
    // Kampagnen-Nachrichten cachen unter eigenem Schlüssel, damit ein vor dem Update
    // (ohne Kampagne) gecachter Code unter voucher_<id> nicht mehr angezeigt wird.
    const cacheKey = campaign ? `${messageId}_${campaign}` : `${messageId}`;
    const localStorageKey = `voucher_${cacheKey}`;
    if (voucherCache.value[cacheKey]) return voucherCache.value[cacheKey];

    let code = localStorage.getItem(localStorageKey);
    if (!code) {
      try {
        const { currentUser } = useCurrentUser();
        const res = await api.get("/vouchers/random", {
          params: { messageId: `message_${messageId}`, campaign, userId: currentUser.value?.id },
        });
        code = res.data.code || "—";
        localStorage.setItem(localStorageKey, res.data.code ?? "—");
      } catch (err) {
        console.error("Voucher API error:", err);
        code = "—";
      }
    }

    voucherCache.value[cacheKey] = code || "—";
    return code;
  };

  const unreadMessages = computed(() => messages.value.filter((msg) => !readMessageIds.value.includes(msg.id)));
  const unreadCount = computed(() => unreadMessages.value.length);

  return {
    messages,
    unreadMessages,
    unreadCount,
    fetchMessages,
    markAsRead,
    getVoucherCode,
  };
}
